import React, { useEffect, useState } from 'react'
import { Doughnut } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
} from "chart.js";
import { useDispatch, useSelector } from 'react-redux';
import { getCapitalList } from '../../actions/capital';
import CurrencyFormat from '../../components/currencyFormat/currencyFormat';
import { CUSTOM_SELECT_STYLES, HOLDERS, PUSH_NOTIFICATION_MODULE, SELECT_BOX_THEME } from '../../constants';
import Select from "react-select";
import { Controller, useForm } from 'react-hook-form';
import { getShareHolderList } from '../../actions/shareHolder';
import { getOfficerList } from '../../actions/officer';
import { getPromoterAction } from '../../actions/promoters';
import { useNavigate } from 'react-router';
import { RoutesPaths } from '../../Router/constant';
import { addChannel, getTwilioToken } from '../../actions/chat';
import { Client } from '@twilio/conversations';
import { validateEnablePayCapital } from '../payment/PaidUpCapital';
import { createChat } from '../../services/api';

ChartJS.register(ArcElement, Tooltip, Legend);

export default function PaidupCapitalComponent({ pageType }) {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
        watch,
        setValue,
        reset
    } = useForm();

    watch(["selectedPayer"]);
    const { officers, pendingOfficerList } = useSelector((state) => state.officers);
    const { shareHolder, pendingShareholderList } = useSelector((state) => state.shareHolder);
    const { user } = useSelector((state) => state.auth);
    const { paidUpCapital, paidUpAmountPaid, recentAddedCapital } = useSelector((state) => state.capital);
    const { mergerPromoter } = useSelector((state) => state.promoters);
    const [conversationLoader, setConversationLoader] = useState(false);
    const totalShareCapital = paidUpCapital.reduce((total, item) => total + (item.shareCapital || 0), 0);
    const totalPaidShareCapital = paidUpAmountPaid.reduce((total, item) => total + (item.paidAmount || 0), 0);

    const paidPercentage = totalShareCapital > 0
        ? ((totalPaidShareCapital / totalShareCapital) * 100).toFixed(2)
        : 0;

    const unpaidPercentage = (100 - paidPercentage).toFixed(2);

    const [activeIndex, setActiveIndex] = useState(null);

    const { selectedCompany } = useSelector((state) => state.company);

    useEffect(() => {
        const { id } = selectedCompany;
        if (id) {
            dispatch(getCapitalList(id));
            dispatch(getPromoterAction(id));
            dispatch(getShareHolderList(id));
            dispatch(getOfficerList(id));

        }
    }, [dispatch, selectedCompany]);

    useEffect(() => {
        if (!recentAddedCapital || !paidUpCapital?.length) return;

        const matched = paidUpCapital.find(
            (item) =>
                item.currency?.toLowerCase() === recentAddedCapital.currency?.toLowerCase() &&
                parseInt(item.shareType) === recentAddedCapital.shareType
        );

        const index = paidUpCapital.findIndex((item) => item.id === matched?.id);
        setActiveIndex(index);
    }, [paidUpCapital, recentAddedCapital]);

    const promoters = mergerPromoter || [];
    const officerList = officers || [];
    const shareholderList = shareHolder || [];

    const pendingEmails = new Set(pendingShareholderList.map(x => x.email));
    const pendingOfficerEmails = new Set(pendingOfficerList.map(x => x.email));

    const activeShareholders = shareholderList.filter(user => !pendingEmails.has(user.email));
    const activeOfficers = officerList.filter(user => !pendingOfficerEmails.has(user.email));
    const activePromoters = promoters.filter(user => 'isEmailVerified' in user && user?.isEmailVerified);

    // Combine all active users into one list
    const combinedActiveUsers = [...activePromoters, ...activeShareholders, ...activeOfficers];

    // Deduplicate by email (first occurrence is kept)
    const uniqueActiveUsersMap = new Map();
    combinedActiveUsers.forEach(user => {
        if (!uniqueActiveUsersMap.has(user.email)) {
            uniqueActiveUsersMap.set(user.email, user);
        }
    });
    const uniqueActiveUsers = Array.from(uniqueActiveUsersMap.values());
    const userList = uniqueActiveUsers;

    const defaultPayer = selectedCompany?.promoters?.filter((item) => item?.email === user?.email)[0]
    useEffect(() => {
        setValue("selectedPayer", defaultPayer)
    }, [setValue, defaultPayer])

    const paid = paidPercentage;
    const unpaid = unpaidPercentage;

    const data = {
        labels: ["Paid", "Unpaid"],
        datasets: [
            {
                data: [paidPercentage, unpaidPercentage], // total should be 100
                backgroundColor: ["#22c55e", "#fcd34d"],
                borderWidth: 5,
                borderRadius: 10,
                circumference: 180,
                rotation: 270,
                cutout: "85%",
            },
        ],
    };

    const options = {
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                displayColors: false,
                callbacks: {
                    label: function (ctx) {
                        return `${ctx.label}: ${ctx.formattedValue}%`;
                    },
                },
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        hoverBorderColor: "white",
    };

    const toggleItem = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const onSubmit = async (data) => {
        if (data?.selectedPayer?.email === user?.email) {
            if (pageType) {
                navigate(RoutesPaths.PAIDUPDETAILPAYMENT.replace(":slug", paidUpCapital[activeIndex]?.id));
            } else {
                navigate(RoutesPaths.PAY_PAIDUP_PAGE.replace(":slug", paidUpCapital[activeIndex]?.id))
            }
        } else {
            setConversationLoader(true);
            const members = [(data?.selectedPayer?.position === HOLDERS.shareHolders || data?.selectedPayer?.position === HOLDERS.directors) ? data?.selectedPayer?.userId : data?.selectedPayer?.id || data?.selectedPayer?._id]
            var obj = {
                company: selectedCompany?.id,
                participants: members,
                module: PUSH_NOTIFICATION_MODULE.PAIDUPLINK, // Custom attribute
                data: paidUpCapital[activeIndex],
                message: data?.message,
            };


            var channelRes = await createChat(obj);
            setConversationLoader(false);
            navigate(RoutesPaths.MESSAGE_DETAIL + '?room=' + channelRes._id);

        }
    }

    const AccordionItem = ({ item, isOpen, onToggle }) => (
        <div className="accordion-item">
            <div className={`${isOpen ? "active-header" : ""} accordion-header accordion-custom-header`} onClick={onToggle}>
                <div className="row-content table-accordion-content">
                    <div className="col-content"><p className='accountSecondaryText'><CurrencyFormat value={validateEnablePayCapital(item, paidUpAmountPaid, true)} /></p></div>
                    <div className="col-content"><p className='accountSecondaryText'>{item.currency}</p></div>
                    <div className="col-content arrow-img-col-holder">
                        <p className='accountSecondaryText'>{item.shareType === 1 ? "Ordinary" : item.shareType === 2 ? "Preference" : ""}</p>
                        <div className="arrow-img-col"><img className={`accordion-img ${isOpen ? 'imgRotate' : ''}`} src="assets/img/accordion-arrow.svg" alt="" /></div></div>

                </div>
            </div>
            {isOpen && <div className="accordion-body accordion-body-content">
                <p className='accountPrimaryText'>Select who to pay paid-up capital</p>
                <div className='select-box-holder'>
                    <Controller
                        render={({ field }) => (
                            <Select
                                {...field}
                                theme={SELECT_BOX_THEME}
                                styles={CUSTOM_SELECT_STYLES}
                                className="select-border"
                                options={userList}
                                getOptionLabel={(option) => option?.position === HOLDERS.shareHolders || option?.position === HOLDERS.directors ? option.name : option.first_name + " " + option.last_name}
                                getOptionValue={(option) => option.email}
                                placeholder="Choose payer"
                            />
                        )}
                        name="selectedPayer"
                        control={control}
                    />
                </div>
                <p className='accountSecondaryText'>Here you can select who to pay for the share capital into {selectedCompany?.companyName} for shares the company is issuing</p>
                {getValues("selectedPayer") &&
                    <>
                        <div className="selectedPayer">
                            <img src="assets/img/Frame1.svg" alt="" />
                            <p>{getValues("selectedPayer")?.position === HOLDERS.shareHolders || getValues("selectedPayer")?.position === HOLDERS.directors ? getValues("selectedPayer")?.name : getValues("selectedPayer")?.first_name + " " + getValues("selectedPayer")?.last_name}</p>
                        </div>
                        {getValues("selectedPayer")?.email !== user?.email &&
                            <div className="messageHolderPaidUp">
                                <p className='messageText'>Message</p>
                                <Controller
                                    name="message"
                                    rules={{ required: true }}
                                    control={control}
                                    render={({ field }) => (
                                        <textarea
                                            {...field}
                                            maxLength={50}
                                            type="text"
                                            name="message"
                                            className="form-control messageTextarea"
                                        />
                                    )}
                                />
                                {errors.message?.type === "required" && (
                                    <span className="errorMessage">
                                        Enter message
                                    </span>
                                )}
                            </div>
                        }
                        <div className='footer-btn'>
                            <button disabled={conversationLoader || validateEnablePayCapital(item, paidUpAmountPaid)} onClick={handleSubmit(onSubmit)} className='customFilterButton mobileViewImportButton'>
                                {conversationLoader && (<i className="animateSpin fa-solid fa-circle-notch"></i>)} {getValues("selectedPayer")?.email !== user?.email ? "Send Instructions" : "Initiate payment"}
                            </button>
                        </div>
                    </>
                }
            </div>
            }
        </div>
    );

    return (
        <>
            <div className={`${pageType ? "pt-0" : ""} container-main`}>
                {!pageType &&
                    <h3 className='primaryHeader'>Now fund your company</h3>
                }
                <div className={`${pageType ? "mt-0" : ""} graph-holder`}>
                    <div className='row'>
                        <div className="col-12 col-md-6" style={{ position: "relative", }}>
                            <Doughnut data={data} options={options} />

                            <div className='graph-content-Holder'>
                                <span className='graph-content'> {paid}% Shares paid</span>
                                <br />
                                <span className='graph-content'> {unpaid}% Shares unpaid</span>
                            </div>
                        </div>
                        <div className="col-12 col-md-6">
                            <ul className='option-list-holder'>
                                {["Paid", "Unpaid"].map((label, index) => (
                                    <li key={label} className='option-list'>
                                        <p>
                                            <span
                                                style={{
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: "50%",
                                                    backgroundColor: data.datasets[0].backgroundColor[index],
                                                    marginRight: '5px'
                                                }}
                                            />
                                            {label}
                                        </p>
                                        <p>{data.datasets[0].data[index]}%</p>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className='paid-up-content-card'>
                <div className='card-header-content'>
                    <h6 className='secondryHeader'>Now fund your company</h6>
                    <p className='paid-up-content-desc'>Keep track of share capitals</p>
                </div>
                <div className='table-header-content'>
                    <div className="row-content">
                        <div className="col-content"><p className='accountPrimaryText'>Paid-up capital amount</p></div>
                        <div className="col-content"><p className='accountPrimaryText'>Currency</p></div>
                        <div className="col-content"><p className='accountPrimaryText'>Share type</p></div>
                    </div>
                </div>
                <div className="accordion">
                    {paidUpCapital.map((item, index) => (
                        <AccordionItem
                            key={index}
                            item={item}
                            isOpen={activeIndex === index}
                            onToggle={() => toggleItem(index)}
                        />
                    ))}
                </div>
            </div>

        </>
    )
}
